import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Upload, User, BookOpen, Award, Trash2, Key } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

import { useLanguage } from "@/contexts/LanguageContext";

interface Profile {
  id: string;
  full_name: string;
  avatar_url: string | null;
  bio: string | null;
}

interface Enrollment {
  id: string;
  progress: number;
  status: string;
  enrolled_at: string;
  course: {
    id: string;
    title: string;
    slug: string;
    thumbnail_url: string | null;
    description: string | null;
  };
}

interface Certificate {
  id: string;
  certificate_url: string | null;
  issued_at: string;
  course: {
    title: string;
  };
}

const Account = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    bio: "",
  });
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loadingLearning, setLoadingLearning] = useState(true);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  const defaultTab = searchParams.get("tab") || "profile";

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchLearningData();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user?.id)
        .single();

      if (error) throw error;

      setProfile(data);
      setFormData({
        full_name: data.full_name || "",
        bio: data.bio || "",
      });
    } catch (error: any) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setProfile((prev) => prev ? { ...prev, avatar_url: publicUrl } : null);
      toast.success("Avatar updated successfully");
    } catch (error: any) {
      toast.error("Failed to upload avatar");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: formData.full_name,
          bio: formData.bio,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast.success("Profile updated successfully");
      fetchProfile();
    } catch (error: any) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const fetchLearningData = async () => {
    if (!user) return;

    try {
      // Fetch enrollments with course details
      const { data: enrollmentsData, error: enrollmentsError } = await supabase
        .from("enrollments")
        .select(`
          id,
          progress,
          status,
          enrolled_at,
          course:courses(
            id,
            title,
            slug,
            thumbnail_url,
            description
          )
        `)
        .eq("user_id", user.id)
        .order("enrolled_at", { ascending: false });

      if (enrollmentsError) throw enrollmentsError;
      setEnrollments(enrollmentsData || []);

      // Fetch certificates
      const { data: certificatesData, error: certificatesError } = await supabase
        .from("certificates")
        .select(`
          id,
          certificate_url,
          issued_at,
          course:courses(
            title
          )
        `)
        .eq("user_id", user.id)
        .order("issued_at", { ascending: false });

      if (certificatesError) throw certificatesError;
      setCertificates(certificatesData || []);
    } catch (error: any) {
      toast.error("Failed to load learning data");
    } finally {
      setLoadingLearning(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (error) throw error;

      toast.success("Password updated successfully");
      setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error: any) {
      toast.error("Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setDeletingAccount(true);
    try {
      // Call edge function to delete account (requires service role key)
      const { error } = await supabase.functions.invoke('delete-account');

      if (error) throw error;

      toast.success("Account deleted successfully");
      navigate("/");
    } catch (error: any) {
      toast.error("Failed to delete account. Please contact support.");
    } finally {
      setDeletingAccount(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-y-auto">
      <Navigation />
      <main className="container mx-auto px-4 py-24 pb-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">{t("account.title")}</h1>
          <p className="text-muted-foreground mb-8">{t("account.subtitle")}</p>

          <Tabs defaultValue={defaultTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="profile">{t("account.profileTab")}</TabsTrigger>
              <TabsTrigger value="learning">{t("account.learningTab")}</TabsTrigger>
              <TabsTrigger value="settings">{t("account.settingsTab")}</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle>{t("account.profileTitle")}</CardTitle>
                  <CardDescription>{t("account.profileDesc")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-6">
                    <Avatar className="h-24 w-24">
                      <AvatarImage src={profile?.avatar_url || ""} />
                      <AvatarFallback>
                        <User className="h-12 w-12" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <Label htmlFor="avatar-upload" className="cursor-pointer">
                        <div className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
                          {uploading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                          {uploading ? t("account.uploading") : t("account.uploadAvatar")}
                        </div>
                      </Label>
                      <Input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarUpload}
                        disabled={uploading}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        {t("account.avatarHint")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t("account.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ""}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("account.emailHint")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="full_name">{t("account.fullName")}</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) =>
                        setFormData({ ...formData, full_name: e.target.value })
                      }
                      placeholder={t("account.fullNamePlaceholder")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">{t("account.bio")}</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) =>
                        setFormData({ ...formData, bio: e.target.value })
                      }
                      placeholder={t("account.bioPlaceholder")}
                      rows={4}
                    />
                  </div>

                  <Button onClick={handleSaveProfile} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t("account.saveChanges")}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="learning" className="space-y-6">
              {/* Enrolled Courses */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    {t("account.myCourses")}
                  </CardTitle>
                  <CardDescription>{t("account.trackProgress")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingLearning ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : enrollments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{t("account.noEnrollments")}</p>
                      <Button
                        className="mt-4"
                        onClick={() => navigate("/courses")}
                      >
                        {t("account.browseCourses")}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {enrollments.map((enrollment) => (
                        <div
                          key={enrollment.id}
                          className="flex gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                        >
                          {enrollment.course.thumbnail_url && (
                            <img
                              src={enrollment.course.thumbnail_url}
                              alt={enrollment.course.title}
                              className="w-24 h-24 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 space-y-2">
                            <h3 className="font-semibold">
                              {enrollment.course.title}
                            </h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {enrollment.course.description}
                            </p>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{t("account.progress")}</span>
                                <span className="font-medium">{enrollment.progress}%</span>
                              </div>
                              <Progress value={enrollment.progress} />
                            </div>
                            <Button
                              size="sm"
                              onClick={() => navigate(`/courses/${enrollment.course.slug}/learn`)}
                            >
                              {t("account.continueLearning")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Certificates */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    {t("account.myCertificates")}
                  </CardTitle>
                  <CardDescription>{t("account.achievements")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {certificates.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{t("account.noCertificates")}</p>
                      <p className="text-sm mt-2">
                        {t("account.earnCertificates")}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {certificates.map((certificate) => (
                        <div
                          key={certificate.id}
                          className="p-4 border rounded-lg space-y-2"
                        >
                          <div className="flex items-start justify-between">
                            <Award className="h-8 w-8 text-primary" />
                            {certificate.certificate_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  window.open(certificate.certificate_url!, "_blank")
                                }
                              >
                                {t("account.view")}
                              </Button>
                            )}
                          </div>
                          <h3 className="font-semibold">
                            {certificate.course.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {t("account.issuedOn")}{" "}
                            {new Date(certificate.issued_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              {/* Change Password */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    {t("account.changePassword")}
                  </CardTitle>
                  <CardDescription>{t("account.updatePassword")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new_password">{t("account.newPassword")}</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      placeholder={t("account.newPassword")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">{t("account.confirmPassword")}</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder={t("account.confirmPassword")}
                    />
                  </div>

                  <Button
                    onClick={handleChangePassword}
                    disabled={
                      changingPassword ||
                      !passwordData.newPassword ||
                      !passwordData.confirmPassword
                    }
                  >
                    {changingPassword && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {t("account.updatePasswordBtn")}
                  </Button>
                </CardContent>
              </Card>

              {/* Delete Account */}
              <Card className="border-destructive">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <Trash2 className="h-5 w-5" />
                    {t("account.deleteAccount")}
                  </CardTitle>
                  <CardDescription>
                    {t("account.deleteAccountDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={deletingAccount}>
                        {deletingAccount && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {t("account.deleteAccount")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("account.deleteAccount")}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("account.deleteWarning")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAccount}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {t("account.deleteMyAccount")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Account;
